// src/lib/api_meta_oauth.ts
// Cliente para el OAuth per-org de Meta (backend: apps/meta/routers/oauth.py).
// Flujo: start → redirige al consent → callback hace exchange → opcional select-page.

const DJANGO_PROXY = '/django-api';

import { ensureFreshToken } from '@/lib/tokenRefresh';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada.');
  return token;
}

async function authHeaders(): Promise<Record<string, string>> {
  return {
    Authorization: `Bearer ${await getJwt()}`,
  };
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = await authHeaders();
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) { headers['Content-Type'] = 'application/json'; }
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init, headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    if (res.status === 401) throw new ApiError('Sesión expirada.', 401);
    throw new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }
  return res.json();
}

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

export interface PageChoice {
  id: string;
  name: string;
  category: string;
  has_instagram: boolean;
  tasks: string[];
}

export interface AdAccountChoice {
  id: string;
  account_id: string;
  name: string;
  business_name: string;
  currency: string;
  account_status: number | null;
}

export interface MetaAuthStatus {
  connected: boolean;
  selected_page_id: string;
  selected_page_name: string;
  ig_business_id: string;
  ad_account_id: string;
  waba_id: string;
  wa_phone_number_id: string;
  granted_scopes: string;
  user_token_set: boolean;
  page_token_set: boolean;
  connected_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  error_message: string;
  available_pages: PageChoice[];
  available_ad_accounts: AdAccountChoice[];
}

// ═══════════════════════════════════════════════════════════════
// API
// ═══════════════════════════════════════════════════════════════

export async function startMetaOAuth(): Promise<{ authorization_url: string; state: string }> {
  // frontend_origin se firma en el state para que el callback público del
  // backend (/api/v1/oauth/meta/callback) sepa a dónde 302-redirect post-
  // exchange. Esto habilita el patrón single-callback: Meta Dev Console
  // solo registra UN URL del backend, multi-tenant escala infinito.
  return djangoFetch('/api/v1/admin/meta/oauth/start', {
    method: 'POST',
    body: JSON.stringify({ frontend_origin: window.location.origin }),
  });
}

export async function exchangeMetaOAuth(code: string, state: string): Promise<MetaAuthStatus> {
  return djangoFetch('/api/v1/admin/meta/oauth/exchange', {
    method: 'POST', body: JSON.stringify({ code, state }),
  });
}

export async function selectMetaPage(page_id: string): Promise<MetaAuthStatus> {
  return djangoFetch('/api/v1/admin/meta/oauth/select-page', {
    method: 'POST', body: JSON.stringify({ page_id }),
  });
}

export async function selectMetaAdAccount(ad_account_id: string): Promise<MetaAuthStatus> {
  return djangoFetch('/api/v1/admin/meta/oauth/select-ad-account', {
    method: 'POST', body: JSON.stringify({ ad_account_id }),
  });
}

export async function getMetaAuthStatus(): Promise<MetaAuthStatus> {
  return djangoFetch('/api/v1/admin/meta/auth');
}

export async function disconnectMetaOAuth(): Promise<{ revoked: boolean; detail: string }> {
  return djangoFetch('/api/v1/admin/meta/auth/disconnect', {
    method: 'POST', body: '{}',
  });
}
